import { Box } from '@customink/pigment-react';
import {
  isMultipleDeliveryOptions,
  useProject,
} from '@customink/project-service-react-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { groupBy } from './util';

import { modalStateAtom } from '@/atoms/editSizesModalAtoms';
import InvalidZipCode from '@/components/Alerts/InvalidZipCode';
import MultipleDeliveryOptions from '@/components/Alerts/MultipleDeliveryOptions';
import CatalogIframe from '@/components/CatalogIframe';
import DeliveryOptions from '@/components/DeliveryOptions/DeliveryOptions';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ProjectItemGroup } from '@/components/ProjectItemGroup';
import { useZipCodeValid } from '@/components/ProjectItemList/hooks';
import { ProjectItemSkeleton } from '@/components/ProjectItemSkeleton';
import { useViewCartEvent } from '@/hooks/Analytics/useViewCartEvent';
import { useHasMultipleDesigns } from '@/hooks/Design';
import { spacing16, spacing24 } from '@/utils/designTokens';

// TODO: the CatalogIframe could reasonably be a top-level exported component
// we'll likely only ever want one instance of this element

export function ProjectItemList() {
  const { data: project } = useProject();
  useViewCartEvent();
  const setModalState = useSetAtom(modalStateAtom);
  const modalState = useAtomValue(modalStateAtom);

  const hasMultipleDesigns: boolean = useHasMultipleDesigns();
  const { zipCodeValid } = useZipCodeValid();

  const isEmpty = !project?.items?.length;
  const projectItemGroups = groupBy(
    project?.items || [],
    (item) => item.design?.cid,
  );
  const showDeliveryAlert = isMultipleDeliveryOptions(project?.items);

  // TODO: verify that item order is consistent within projectItemGroups

  useEffect(() => {
    const groupWithZeroQuantity = [...projectItemGroups.values()].find(
      (group) => group.every((item) => item.totalQuantity === 0),
    );

    const designCid = groupWithZeroQuantity?.[0]?.design?.cid;
    if (designCid && zipCodeValid) {
      setModalState({
        designCid,
        source: 'initial_load',
      });
    }

    return () => {
      setModalState(null);
    };
  }, []);

  const handleModalClose = () => {
    setModalState(null);
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <ErrorBoundary>
        {!!showDeliveryAlert && <MultipleDeliveryOptions />}
        {!zipCodeValid && <InvalidZipCode />}
        <Box
          data-testid="project-item-list"
          display="flex"
          flexDirection="column"
          gap={spacing24}
          paddingBottom={spacing16}
        >
          {!project && <ProjectItemSkeleton />}
          {!!isEmpty && <>This cart has nothing in it</>}

          {[...projectItemGroups.values()].map((group) => {
            const groupDesignCid = group?.[0].design?.cid ?? '';
            return (
              <ProjectItemGroup
                designCid={groupDesignCid}
                isModalOpen={modalState?.designCid === groupDesignCid}
                key={groupDesignCid}
                onCloseModal={() => handleModalClose()}
                onOpenModal={setModalState}
                projectItems={group}
              />
            );
          })}

          {hasMultipleDesigns && <DeliveryOptions />}
        </Box>
        <CatalogIframe />
      </ErrorBoundary>
    </Box>
  );
}
